import { Component, Input } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { VGSECheckInfo } from 'apps/public-portal/src/app/payment-integrations/base/models';
import {
    PaymentIntegrationFieldValue,
} from 'apps/public-portal/src/app/payment-integrations/base/components/payment-integration-form-field/payment-integration-form-field.component';
import { VgsCollectBase } from './vgs-collect-base';

@Component({
    selector: 'app-vgs-collect-echeck',
    templateUrl: './vgs-collect-echeck.component.html',
    styleUrls: ['./vgs-collect.component.scss', './vgs-collect-echeck.component.scss'],
    host: {
        class: 'vgs-collect payment-method-details payment-method-details--echeck'
    },
})
export class VgsCollectEcheckComponent extends VgsCollectBase {
    @Input() public ECHECK_FIELDS: {
        checkname: string,
        checktype: string,
        checkowner: string,
        checkaba: string,
        checkaccount: string,
        checkabavalidator: string,
        checkaccountvalidator: string,
    };

    @Input() public ECHECK_ACCOUNTTYPELIST: string[];

    @Input() public ECHECK_ACCOUNTOWNERTYPELIST: string[];

    @Input() public accountTypeEnabled: boolean = true;

    @Input() public accountOwnerTypeEnabled: boolean = false;

    @Input() public requireDualEntryForECheck: boolean = false;

    protected initFormControls(): void {
        this.formGroup.addControl('checkaba', new UntypedFormControl(
            new PaymentIntegrationFieldValue(null, '', 'checkaba', this.ECHECK_FIELDS.checkaba)
        ));
        this.formGroup.addControl('checkaccount', new UntypedFormControl(
            new PaymentIntegrationFieldValue(null, '', 'checkaccount', this.ECHECK_FIELDS.checkaccount)
        ));
        this.formGroup.addControl(this.ECHECK_FIELDS.checkname, new UntypedFormControl(this.paymentMethodData.billingInfo.fullName));

        if (this.accountTypeEnabled) {
            this.formGroup.addControl(this.ECHECK_FIELDS.checktype, new UntypedFormControl(this.paymentMethodData.echeckAccountType));
        }
        if (this.accountOwnerTypeEnabled) {
            this.formGroup.addControl(this.ECHECK_FIELDS.checkowner, new UntypedFormControl(this.paymentMethodData.echeckAccountOwnerType));
        }
        if (this.requireDualEntryForECheck) {
            // Confirmation fields are validated entirely inside the VGS iframe via the
            // compareValue validator; the parent form-control just tracks validity.
            this.formGroup.addControl('checkabavalidator', new UntypedFormControl(
                new PaymentIntegrationFieldValue(null, '', 'checkabavalidator', this.ECHECK_FIELDS.checkabavalidator)
            ));
            this.formGroup.addControl('checkaccountvalidator', new UntypedFormControl(
                new PaymentIntegrationFieldValue(null, '', 'checkaccountvalidator', this.ECHECK_FIELDS.checkaccountvalidator)
            ));
        }

        this._formChangeSubscription = this.formGroup.valueChanges.subscribe(value => {
            this.paymentMethodData.billingInfo.fullName = value[this.ECHECK_FIELDS.checkname];
            if (this.accountTypeEnabled) {
                this.paymentMethodData.echeckAccountType = value[this.ECHECK_FIELDS.checktype];
            }
            if (this.accountOwnerTypeEnabled) {
                this.paymentMethodData.echeckAccountOwnerType = value[this.ECHECK_FIELDS.checkowner];
            }
        });
    }

    // CSS injected into the VGS iframe to match Material input appearance
    private static readonly VGS_FIELD_CSS = {
        'font-size': '16px',
        'font-family': 'inherit',
        padding: '0',
        margin: '0',
        'line-height': '1.125',
        'box-sizing': 'border-box',
    };

    protected initVGSFields(): void {
        this._vgsCollectForm.field('#vgsRoutingNumber', {
            type: 'text',
            name: 'routingNumber',
            placeholder: '000000000',
            validations: ['required', '/^\\d{9}$/'],
            css: VgsCollectEcheckComponent.VGS_FIELD_CSS,
        });
        this._vgsCollectForm.field('#vgsAccountNumber', {
            type: 'text',
            name: 'accountNumber',
            placeholder: '000000000',
            validations: ['required', '/^\\d{4,17}$/'],
            css: VgsCollectEcheckComponent.VGS_FIELD_CSS,
        });

        if (this.requireDualEntryForECheck) {
            // Confirmation fields use VGS Collect's `compareValue` validator
            // (https://docs.verygoodsecurity.com/vault/developer-tools/vgs-collect/js/reference-documentation,
            // available since v2.4.0). The comparison runs entirely inside the
            // VGS iframe — neither value crosses the iframe boundary, and the
            // *Confirm fields don't appear in the submit response (the inbound
            // proxy receives only routingNumber / accountNumber).
            this._vgsCollectForm.field('#vgsRoutingNumberConfirm', {
                type: 'text',
                name: 'routingNumberConfirm',
                placeholder: '000000000',
                validations: [
                    'required',
                    '/^\\d{9}$/',
                    { type: 'compareValue', params: { field: 'routingNumber', function: 'match' } },
                ],
                css: VgsCollectEcheckComponent.VGS_FIELD_CSS,
            });
            this._vgsCollectForm.field('#vgsAccountNumberConfirm', {
                type: 'text',
                name: 'accountNumberConfirm',
                placeholder: '000000000',
                validations: [
                    'required',
                    '/^\\d{4,17}$/',
                    { type: 'compareValue', params: { field: 'accountNumber', function: 'match' } },
                ],
                css: VgsCollectEcheckComponent.VGS_FIELD_CSS,
            });
        }
    }

    protected mapVGSResponse(data: VGSECheckInfo): void {
        this.paymentMethodData.echeckAccountNumber = data.accountNumber;
        this.paymentMethodData.echeckRoutingNumber = data.routingNumber;

        this.formGroup.controls.checkaccount.setValue(new PaymentIntegrationFieldValue(data.accountNumber, ''));
        this.formGroup.controls.checkaba.setValue(new PaymentIntegrationFieldValue(data.routingNumber, ''));
    }

    protected getValidationFieldMappings(): { formControl: string, vgsField: string, label: string }[] {
        const mappings = [
            { formControl: 'checkaba', vgsField: 'routingNumber', label: this.ECHECK_FIELDS.checkaba },
            { formControl: 'checkaccount', vgsField: 'accountNumber', label: this.ECHECK_FIELDS.checkaccount },
        ];
        if (this.requireDualEntryForECheck) {
            mappings.push(
                { formControl: 'checkabavalidator', vgsField: 'routingNumberConfirm', label: this.ECHECK_FIELDS.checkabavalidator },
                { formControl: 'checkaccountvalidator', vgsField: 'accountNumberConfirm', label: this.ECHECK_FIELDS.checkaccountvalidator },
            );
        }
        return mappings;
    }

    public generateAdditionalErrors(): { [key: string]: string } {
        const errors: { [key: string]: string } = {};
        if (this.formGroup.controls.checkaba?.value?.error) errors.checkaba = this.formGroup.controls.checkaba.value.error;
        if (this.formGroup.controls.checkaccount?.value?.error) errors.checkaccount = this.formGroup.controls.checkaccount.value.error;
        if (this.requireDualEntryForECheck) {
            if (this.formGroup.controls.checkabavalidator?.value?.error) {
                errors.checkabavalidator = this.formGroup.controls.checkabavalidator.value.error;
            }
            if (this.formGroup.controls.checkaccountvalidator?.value?.error) {
                errors.checkaccountvalidator = this.formGroup.controls.checkaccountvalidator.value.error;
            }
        }
        return errors;
    }
}
